export enum CATEGORY {
  CABINETS = 'cabinets',
  INTERNAL_LAYINGS = 'internalLayings',
  LINEARS = 'linears',
  APPLIANCES = 'appliances',
  SANITARIES = 'sanitaries',
}

export class BomDataHelper {
  static orderedCatList: string[] = [
    CATEGORY.CABINETS,
    CATEGORY.INTERNAL_LAYINGS,
    CATEGORY.LINEARS,
    CATEGORY.APPLIANCES,
    CATEGORY.SANITARIES,
  ];

  static _eanRgx: RegExp = /^([\d]{13})$/;

  static parseBomData(data: any): any {
    if (typeof data !== 'string') {
      return data;
    }

    const trimmedData = data.trim();
    if (!trimmedData) {
      return undefined;
    }

    try {
      return JSON.parse(trimmedData);
    } catch {
      return undefined;
    }
  }

  static isBomEmpty(data: any): boolean {
    const bom = this.parseBomData(data);

    if (!bom || typeof bom !== 'object') {
      return true;
    }

    if (Object.keys(bom).length === 0) {
      return true;
    }

    if (this.hasNormalizedBlocks(bom)) {
      return !this.hasRowsWithQuantity(bom.blocks);
    }

    if (this.hasRawBomCollections(bom)) {
      return this.extractFlatProductsList(bom, false).products.length === 0;
    }

    return bom.empty !== false;
  }

  static hasUnavailablePrice(data: any): boolean {
    const bom = this.parseBomData(data);

    if (!bom || typeof bom !== 'object') {
      return false;
    }

    if (bom.unavailablePrice === true) {
      return true;
    }

    if (this.hasRawBomCollections(bom)) {
      return this.extractFlatProductsList(bom, false).unavailablePrice;
    }

    return false;
  }

  static isBomUnavailableForProductList(data: any): boolean {
    return this.isBomEmpty(data) || this.hasUnavailablePrice(data);
  }

  private static hasRawBomCollections(data: any): boolean {
    return ['products', 'linears', 'packs'].some(key => Array.isArray(data?.[key]));
  }

  private static hasNormalizedBlocks(data: any): boolean {
    return Array.isArray(data?.blocks);
  }

  private static hasRowsWithQuantity(blocks: any[]): boolean {
    return blocks.some(block => {
      return Array.isArray(block?.rows) && block.rows.some(row => this.hasPositiveQuantity(row));
    });
  }

  private static hasPositiveQuantity(row: any): boolean {
    const quantity = Number(row?.quantity);
    return Number.isFinite(quantity) && quantity > 0;
  }

  static _reduceChildren(target: any[], item: any, blockIndex: number /*, debugTab:number = 0*/) {
    if (!item) return;
    // let _debugTabSuffix = "";
    // for (let i = 0; i < debugTab; i++) _debugTabSuffix += "\t";
    item.blockIndex = blockIndex;
    if (item.children) {
      if (item.price) {
        let _c = Object.assign({}, item);
        delete _c.children;
        target.push(_c);
        // console.log(_debugTabSuffix+"ADD (priced with children)", _c.name, _c.reference);
      }
      for (let i = 0; i < item.children.length; i++) {
        // console.log(_debugTabSuffix+"--OPEN (children)", item.name, item.reference);
        this._reduceChildren(target, item.children[i], blockIndex /*, (debugTab+1)*/);
      }
    } else {
      if (item.price) {
        // console.log(_debugTabSuffix+"ADD (priced without children)", item.name, item.reference);
        target.push(item);
      } else {
        let _hasEan: boolean = this._eanRgx.test(item.reference);
        // console.log("hasEan",_hasEan, "["+item.reference+"]");
        if (_hasEan) {
          let _c = Object.assign({}, item);
          _c.price = { current: { value: 0 } };
          target.push(_c);
          // console.log(_debugTabSuffix+"ADD : (no priced but ean without children)", _c.name, _c.reference);
        } else {
          // console.log(_debugTabSuffix+"IGNORE : (no priced neither ean without children)", item.name, item.reference,_hasEan);
        }
      }
    }
  }

  static extractBlockGroupedProductsList(data: any) {
    data = this.parseBomData(data);
    //console.log("bom",data);

    let _pList = this.extractFlatProductsList(data, false);

    // console.log("_pList",_pList);

    let _originalBomItems = this.getRawBomItems(data);

    let _r = {
      unavailablePrice: true,
      empty: _pList.products.length === 0,
      blocks: [],
      totalByCategories: this.orderedCatList.map(cat => {
        return { key: cat, deee: 0, ecomob: 0, price: 0 };
      }),
      totalDeee: 0,
      totalEcomob: 0,
      totalPrice: 0,
    };
    if (_pList.unavailablePrice) {
      return _r;
    }

    _r.unavailablePrice = false;

    _pList.products.forEach(p => {
      let _cat: string = this._getCategoryFromIdSmartProduct(p.clientMetadata.idSmartProduct);

      let _block;

      _block = _r.blocks.find(b => b.blockIndex === p.blockIndex);

      if (!_block) {
        let _bomBlock = _originalBomItems[p.blockIndex];

        _block = {
          thumbnailURLSmall: _bomBlock.thumbnailURLSmall != '' ? _bomBlock.thumbnailURLSmall : p.thumbnailURLSmall,
          blockIndex: p.blockIndex,
          title: _bomBlock.name,
          nomenclatureNumber: _bomBlock.nomenclatureNumber ? _bomBlock.nomenclatureNumber : 0,
          order: _bomBlock.nomenclatureNumber ? _bomBlock.nomenclatureNumber : _pList.products.length + p.blockIndex,
          rows: [],
          deee: 0,
          ecomob: 0,
          price: 0,
        };

        _r.blocks.push(_block);
      }
      let _alreadyPopulatedRow = _block.rows.find(row => row.reference === p.reference);
      if (_alreadyPopulatedRow) {
        _alreadyPopulatedRow.quantity += p.quantity || 1;
        _alreadyPopulatedRow.totalPrice += p.totalPrice;
        _alreadyPopulatedRow.totalDeee += p.totalDeee;
        _alreadyPopulatedRow.totalEcomob += p.totalEcomob;
        _alreadyPopulatedRow.computedDepth += p.computedDepth;
        _alreadyPopulatedRow.computedWidth += p.computedWidth;
      } else {
        let _clone = Object.assign({}, p);
        _clone.quantity = p.quantity || 1;
        _block.rows.push(_clone);
      }

      let _totalCat = _r.totalByCategories.find(cat => cat.key == _cat);

      _totalCat.price += p.totalPrice;
      _totalCat.deee += p.totalDeee;
      _totalCat.ecomob += p.totalEcomob;

      _block.price += p.totalPrice;
      _block.deee += p.totalDeee;
      _block.ecomob += p.totalEcomob;

      _r.totalPrice += p.totalPrice;
      _r.totalDeee += p.totalDeee;
      _r.totalEcomob += p.totalEcomob;
    });

    _r.blocks = _r.blocks.sort((a, b) => {
      return a.order - b.order;
    });

    _r.totalByCategories = _r.totalByCategories.filter(cat => {
      return cat.price > 0;
    });

    return _r;
  }

  static updateBeautifiedBomTotalByCategories(beautifiedBom: any) {
    /**
        On veut pouvoir mettre à jour le tableau de TotalByCategories, il faut donc :
        - recréer totalByCategories
        - parcourir chaque block et chaque row pour vérifier leur catégorie (avec _getCategoryFromIdSmartProduct ) puis incrémenter la valeur de price deee et ecomob en fonction de la catégorie
        */

    beautifiedBom.totalByCategories = this.orderedCatList.map(cat => {
      return { key: cat, deee: 0, ecomob: 0, price: 0 };
    });

    beautifiedBom.blocks.forEach(block => {
      block.rows.forEach(row => {
        let _cat: string = this._getCategoryFromIdSmartProduct(row.clientMetadata.idSmartProduct);
        let _totalCat = beautifiedBom.totalByCategories.find(cat => cat.key == _cat);
        _totalCat.price += row.totalPrice;
        _totalCat.deee += row.totalDeee;
        _totalCat.ecomob += row.totalEcomob;
      });
    });

    beautifiedBom.totalByCategories = beautifiedBom.totalByCategories.filter(cat => {
      return cat.price > 0;
    });
  }

  static extractCategoryGroupedProductsList(data: any) {
    let _pList = this.extractFlatProductsList(data);
    let _r = {
      unavailablePrice: true,
      empty: _pList.products.length === 0,

      totalDeee: 0,
      totalEcomob: 0,
      totalPrice: 0,
    };

    this.orderedCatList.map(cat => {
      _r[cat] = [];
      _r[cat + 'TotalPrice'] = 0;
      _r[cat + 'TotalDeee'] = 0;
      _r[cat + 'TotalEcomob'] = 0;
    });

    if (_pList.unavailablePrice) {
      return _r;
    }

    _r.unavailablePrice = false;

    _pList.products.forEach(p => {
      let _cat: string = this._getCategoryFromIdSmartProduct(p.clientMetadata.idSmartProduct);
      _r[_cat].push(p);
      _r[_cat + 'TotalPrice'] += p.totalPrice;
      _r[_cat + 'TotalDeee'] += p.totalDeee;
      _r[_cat + 'TotalEcomob'] += p.totalEcomob;
      _r.totalPrice += p.totalPrice;
      _r.totalDeee += p.totalDeee;
      _r.totalEcomob += p.totalEcomob;
    });

    return _r;
  }

  private static _getCategoryFromIdSmartProduct(idSmartProduct: string): string {
    switch (idSmartProduct) {
      case 'handle':
      case 'leg':
      case 'accessories':
      case 'front3D':
      case 'front_item':
      case 'box':
      case 'box3D':
      case 'boxAssembly':
      case 'drawerAssembly':
      case 'doorAssembly':
      case 'packageBridging':
      case 'packageFiller':
      case 'packageFront':
      case 'cabinet':
      case 'cornerCabinet':
      case 'bridgingAccessories':
      case 'internaldrawerFront':
      case 'packOfpackageBridgingCabinet':
      case 'packOfPackage':
      case 'packOfPackageTU':
      case 'plinth':
      case 'filler': // joint, mastic...
        return CATEGORY.CABINETS;

      case 'worktop': // plan de travail
      case 'worktop_acc':
      case 'worktop_acccase':
        return CATEGORY.LINEARS;

      case 'object3D':
      case 'shelf':
      case 'shelfPack':
      case 'drawerBox':
      case 'drawerBox3D':
      case 'splashbacks': // crédence
        return CATEGORY.INTERNAL_LAYINGS;

      case 'sink': // evier
      case 'sinkAsm':
      case 'vidage':
      case 'taps': // robinets
      case 'tapsAsm':
        return CATEGORY.SANITARIES;

      case 'ovenMicrowave':
      case 'hoods': // hotte
      case 'hobs': // plaque cuisson
      case 'hobsAsm':
      case 'fridgeWasherDishwasher':
        return CATEGORY.APPLIANCES;

      default:
        break;
    }

    return CATEGORY.CABINETS;
  }

  private static getRawBomItems(data: any): any[] {
    if (!data) return [];

    return [
      ...(Array.isArray(data.products) ? data.products : []),
      ...(Array.isArray(data.linears) ? data.linears : []),
      ...(Array.isArray(data.packs) ? data.packs : []),
    ];
  }

  static extractFlatProductsList(data: any, factorize: boolean = true) {
    data = this.parseBomData(data);
    // console.log(data);
    let _r = {
      unavailablePrice: false,
      products: [],
    };

    let _productList = [];
    let _factorisedProductList = [];

    // 1/ regroup all products into one array
    const allItems = this.getRawBomItems(data);

    if (allItems.length === 0) return _r;

    // console.log("allItems", JSON.parse(JSON.stringify(allItems)));

    // 2/ products levels flattening
    allItems.forEach((item, index) => {
      this._reduceChildren(_productList, item, index);
    });
    // console.log("_reduceChildren", JSON.parse(JSON.stringify(_productList)));

    if (_productList.find(product => product.isPriceless || product.isExpired)) {
      _r.unavailablePrice = true;
    }
    // console.log("flattening", JSON.parse(JSON.stringify(_productList)));

    // 3/ cleaning
    _productList = _productList.filter(product => !product.isPriceless && !product.isExpired);

    //console.log("cleaning", JSON.parse(JSON.stringify(_productList)));

    // 4/ factoring
    _productList.map(product => {
      let _alreadyPopulated = _factorisedProductList.find(
        factorisedProduct => factorisedProduct.reference === product.reference
      );
      if (factorize && _alreadyPopulated) {
        _alreadyPopulated.quantity += product.quantity || 1;
        if (_alreadyPopulated.computedWidth && product.computedWidth) {
          _alreadyPopulated.computedWidth += product.computedWidth;
        }
      } else {
        let _clone = Object.assign({}, product);
        _clone.quantity = product.quantity || 1;

        if (_clone.computedWidth && product.computedWidth) {
          _clone.computedWidth = product.computedWidth;
        }
        _factorisedProductList.push(_clone);
      }
    });

    // 5/ taxes
    _factorisedProductList.map(row => {
      row.ecomob = 0;
      row.deee = 0;
      if (row.price.current.additionalData) {
        if (row.price.current.additionalData.ecomob) row.ecomob = parseFloat(row.price.current.additionalData.ecomob);
        if (row.price.current.additionalData.deee) row.deee = parseFloat(row.price.current.additionalData.deee);
      }
    });

    // 6/ pricing
    _factorisedProductList.map(row => {
      row.totalPrice = row.price.current.value * row.quantity;
      row.totalDeee = row.deee * row.quantity;
      row.totalEcomob = row.ecomob * row.quantity;
    });

    _r.products = _factorisedProductList;

    return _r;
  }
}
